import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import {
  getTheme,
  setTheme as persistTheme,
  getTextScale,
  setTextScale as persistTextScale,
  type Theme,
  type TextScale,
} from './lib/theme-prefs'
import { useMediaQuery } from './lib/useMediaQuery'

export type { Theme, TextScale }

/**
 * The scheme actually painted — `system` resolved to one concrete scheme. `data-theme` (set by the
 * effect in App.tsx, beside the existing `lang` sync effect) is always one of these three, never
 * the literal string `system`.
 */
export type ResolvedTheme = 'light' | 'dark' | 'high-contrast'

type Ctx = {
  theme: Theme
  setTheme: (theme: Theme) => void
  /** `theme` with `system` resolved via matchMedia — what App.tsx actually writes to `data-theme`. */
  resolvedTheme: ResolvedTheme
  textScale: TextScale
  setTextScale: (scale: TextScale) => void
}

const ThemeContext = createContext<Ctx | null>(null)

/**
 * Theme + text-size preference. Mirrors i18n.tsx's LangProvider/useLang shape exactly — same file
 * structure, zero new state-management idiom. Persistence is localStorage-only (lib/theme-prefs.ts);
 * there is no backend sync in v1 (see plans/… theming decision) — this provider's setters are the
 * seam where a future `PUT /v1/preferences` call would slot in.
 *
 * `system` is first-class for THEME only — there is no analogous "system" text-size (no such media
 * feature exists, and the rem-based `default` step already defers to the OS/browser text size).
 * When resolving `system`, `prefers-contrast` takes precedence over `prefers-color-scheme`: a
 * member whose OS reports a contrast preference almost always wants our high-contrast scheme
 * regardless of whether that same OS setting also happens to report as dark.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => getTheme())
  const [textScale, setTextScaleState] = useState<TextScale>(() => getTextScale())

  const prefersContrast = useMediaQuery('(prefers-contrast: more)')
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)')

  const resolvedTheme = useMemo<ResolvedTheme>(() => {
    if (theme !== 'system') return theme
    if (prefersContrast) return 'high-contrast'
    if (prefersDark) return 'dark'
    return 'light'
  }, [theme, prefersContrast, prefersDark])

  const setTheme = (next: Theme): void => {
    setThemeState(next)
    persistTheme(next)
  }
  const setTextScale = (next: TextScale): void => {
    setTextScaleState(next)
    persistTextScale(next)
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, textScale, setTextScale }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within <ThemeProvider>')
  return ctx
}
